<script setup>
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconBin from '@/components/icon/IconBin.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import { REGISTRATION_FIELD_TYPES, REGISTRATION_FIELDS_MAX, REGISTRATION_FIELD_LABEL_MAX, REGISTRATION_OPTIONS_MAX, fieldKeyFor } from '@/components/Sites/broadcastAudience'

// Extra questions of the register-to-watch form (runtime `registrationFields`):
// `{ key, label, type, required, options }` rows. Name and email are always
// asked and are not listed here. A row's key is derived from its label while
// the row is new; a saved key never changes, so the Viewers CSV column stays.
const props = defineProps({
	modelValue: {
		type: Array,
		required: true,
	},
	/** Keys already saved on the runtime: their key is frozen. */
	savedKeys: {
		type: Array,
		default: () => [],
	},
	errors: {
		type: Array,
		default: () => [],
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})
const emit = defineEmits(['update:modelValue'])

const keysExcept = (i) => props.modelValue.filter((_, idx) => idx !== i).map((r) => r.key)
const update = (i, patch) => emit('update:modelValue', props.modelValue.map((row, idx) => {
	if (idx !== i) return row
	const next = { ...row, ...patch }
	if ('label' in patch && !props.savedKeys.includes(row.key)) next.key = fieldKeyFor(patch.label, keysExcept(i))
	if (next.type === 'select' && !next.options.length) next.options = ['']
	return next
}))
const remove = (i) => emit('update:modelValue', props.modelValue.filter((_, idx) => idx !== i))
const add = () => {
	if (props.modelValue.length >= REGISTRATION_FIELDS_MAX) return
	emit('update:modelValue', [...props.modelValue, { key: fieldKeyFor('field', props.modelValue.map((r) => r.key)), label: '', type: 'text', required: false, options: [] }])
}
const optionsText = (row) => row.options.join('\n')
const onOptions = (i, text) => update(i, { options: String(text ?? '').split('\n').slice(0, REGISTRATION_OPTIONS_MAX + 1) })
</script>

<template>
	<div class="flex flex-col gap-2" data-testid="registration-fields-editor">
		<div v-for="(row, i) in modelValue" :key="i" class="reg-field min-w-0 rounded-lg border border-surface-4 p-3" :data-testid="`registration-field-${i}`">
			<div class="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-end">
				<FormInput :id="`registration-field-label-${i}`" :model-value="row.label" label="Question" placeholder="e.g. Company" input-classes="placeholder:text-surface-6" :maxlength="REGISTRATION_FIELD_LABEL_MAX" :disabled="disabled" class="min-w-0 flex-1" @update:model-value="update(i, { label: $event })" />
				<FormSelect :id="`registration-field-type-${i}`" :model-value="row.type" label="Type" :options="REGISTRATION_FIELD_TYPES" :disabled="disabled" class="min-w-0 sm:w-36 sm:shrink-0" @update:model-value="update(i, { type: $event })" />
				<FormButton is-icon type="danger" size="md" title="Remove question" :aria-label="`Remove question ${i + 1}`" class="mb-px self-end" :disabled="disabled" :data-testid="`registration-field-remove-${i}`" @click="remove(i)">
					<icon-base :width="16" :height="16" fill="none"><icon-bin /></icon-base>
				</FormButton>
			</div>
			<div v-if="row.type === 'select'" class="mt-2 min-w-0">
				<label :for="`registration-field-options-${i}`" class="mb-1.5 block text-tiny font-medium text-surface-text-1">Options (one per line)</label>
				<textarea :id="`registration-field-options-${i}`" :value="optionsText(row)" rows="3" :disabled="disabled"
					class="w-full rounded-md border border-surface-5 bg-surface-3 px-3 py-2 text-sm text-white placeholder:text-surface-6 focus:border-main focus:outline-none"
					placeholder="Developer&#10;Designer&#10;Other" :data-testid="`registration-field-options-${i}`" @input="onOptions(i, $event.target.value)" />
			</div>
			<div class="mt-2 flex flex-wrap items-center justify-between gap-2">
				<label class="flex cursor-pointer items-center gap-2 text-tiny text-surface-text-1">
					<input type="checkbox" class="size-3.5 accent-main" :checked="row.required" :disabled="disabled" :data-testid="`registration-field-required-${i}`" @change="update(i, { required: $event.target.checked })">
					{{ row.type === 'checkbox' ? 'Must be ticked' : 'Required' }}
				</label>
				<span class="text-tiny text-surface-6" :title="'Column name in the Viewers export'">CSV column: {{ row.label.trim() || row.key }}</span>
			</div>
			<p v-if="errors[i]" class="mb-0 mt-1.5 text-tiny text-error" :data-testid="`registration-field-error-${i}`">{{ errors[i] }}</p>
		</div>
		<p v-if="!modelValue.length" class="mb-0 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="registration-fields-empty">
			Viewers give their name and email. Add questions such as company, job title or ticket type.
		</p>
		<div class="flex flex-wrap items-center gap-3">
			<FormButton type="secondary" size="sm" :disabled="disabled || modelValue.length >= REGISTRATION_FIELDS_MAX" data-testid="registration-field-add" @click="add">
				<icon-base class="mr-1" :width="12" :height="12"><icon-plus /></icon-base>
				Add question
			</FormButton>
			<span class="text-tiny text-surface-7">Up to {{ REGISTRATION_FIELDS_MAX }} questions.</span>
		</div>
	</div>
</template>

<style scoped>
.reg-field :deep(input),
.reg-field :deep(select) {
	border: 1px solid var(--surface-5);
}
.reg-field :deep(input:focus),
.reg-field :deep(select:focus) {
	border-color: var(--c-main);
	box-shadow: 0 0 0 1px var(--c-main);
}
</style>
